import "pages/Products.scss";

import {
    EuiButton,
    EuiFlexGroup,
    EuiFlexItem,
    EuiIcon,
    EuiLink,
    EuiPage,
    EuiPageBody,
    EuiPanel,
    EuiSelect,
    EuiSpacer,
    EuiTab,
    EuiTabs,
    EuiTitle,
} from "@elastic/eui";
import { useFetchLatestProductKindsQuery } from "api/react-query/queries/kinds";
import { useScreensQuery } from "api/react-query/queries/screens";
import { LatestKindsModal } from "components/LatestKindsModal";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { UserContext } from "../App";
import { ProductResults } from "../components/ProductResults";
import { useCurrentShopGroup } from "../utils/PricelistUtils";
import { ShopContext } from "../utils/Shop";
import { capitalizeFirstLetter } from "../utils/Utils";

const baseTabs = [
    {
        id: "cannabis",
        name: "Cannabis",
        href: "/products/cannabis",
        disabled: false,
    },
    {
        id: "horeca",
        name: "Horeca",
        href: "/products/horeca",
        disabled: false,
    },
];

const ProductTabs = ({ tabs, selectedTabId, onSelectTab }) => {
    return (
        <EuiTabs>
            {tabs.map((tab) => (
                <Link to={tab.href} key={tab.id}>
                    <EuiTab onClick={() => onSelectTab(tab.id)} isSelected={tab.id === selectedTabId}>
                        {tab.name}
                    </EuiTab>
                </Link>
            ))}
        </EuiTabs>
    );
};

export const Products = React.memo((props) => {
    // Get shop context
    const { shopId, isUsingMultiScreen } = useContext(ShopContext);
    const defaultTabId = props.showCannabis ? "cannabis" : "horeca";
    const [selectedTabId, setSelectedTabId] = useState(defaultTabId);
    const [selectedScreenId, setSelectedScreenId] = useState("-1");
    const [searchIsOpen, setSearchIsOpen] = useState(false);
    const user = useContext(UserContext);
    const { screenOptions, isLoading } = useScreensQuery();
    const currentShopGroup = useCurrentShopGroup();
    const shopGroupId = currentShopGroup.id;
    const tabs = baseTabs;
    const [isModalVisible, setIsModalVisible] = useState(false);

    const closeModal = useCallback(() => setIsModalVisible(false), []);
    const showModal = useCallback(() => setIsModalVisible(true), []);

    const {
        data: latestKinds,
        isLoading: latestKindsLoading,
        isError: latestKindsIsError,
    } = useFetchLatestProductKindsQuery(shopGroupId);

    // const {data: filteredData, isLoading: loading} = useFetchProductsPagePrices(shopId, selectedTabId)

    const selectScreenOptions = React.useMemo(() => {
        if (isLoading || !isUsingMultiScreen) return null;

        return [
            { value: "-1", text: "Alle schermen" },
            ...screenOptions.map((so) => ({
                value: so.id,
                text: so.name,
            })),
        ];
    }, [screenOptions, isLoading, isUsingMultiScreen]);

    const setSelectedTab = useCallback((id) => {
        setSelectedTabId(id);
    }, []);

    const showAdminLinks = user.profile.is_superuser;
    const canManagePrijsCombi =
        user.profile.is_superuser || user.profile.roles.find((role) => role.name === "operator");
    const selectedTabLabel = tabs.find((tab) => tab.id === selectedTabId)?.name;

    useEffect(() => {
        setSelectedTabId(defaultTabId);
    }, [defaultTabId]);

    return (
        <EuiPage>
            <EuiPageBody component="div">
                <EuiPanel color="transparent" hasBorder={false} hasShadow={false}>
                    <EuiTabs>
                        <ProductTabs tabs={tabs} selectedTabId={selectedTabId} onSelectTab={setSelectedTab} />
                    </EuiTabs>
                    <EuiSpacer />
                    <EuiFlexGroup className="responsiveFlexGroup" alignItems="center" justifyContent="spaceBetween">
                        <EuiFlexItem>
                            <EuiTitle size="l">
                                <h1>{selectedTabLabel ?? capitalizeFirstLetter(selectedTabId)} Producten</h1>
                            </EuiTitle>
                        </EuiFlexItem>
                        <EuiFlexItem grow={false}>
                            <EuiFlexGroup className="responsiveButtonGroup">
                                {isUsingMultiScreen && selectScreenOptions && (
                                    <EuiFlexItem>
                                        <EuiSelect
                                            options={selectScreenOptions}
                                            onChange={(e) => setSelectedScreenId(e.target.value ?? null)}
                                        />
                                    </EuiFlexItem>
                                )}
                                {selectedTabId === "cannabis" && (
                                    <EuiFlexItem grow={false}>
                                        <div>
                                            <EuiButton size="s" onClick={showModal}>
                                                Latest Products
                                            </EuiButton>
                                        </div>
                                    </EuiFlexItem>
                                )}
                                {isModalVisible && (
                                    <LatestKindsModal
                                        closeModal={closeModal}
                                        items={latestKinds}
                                        isLoading={latestKindsLoading}
                                        isError={latestKindsIsError}
                                    />
                                )}
                                <EuiFlexItem grow={false}>
                                    <EuiLink href={`new-product/${selectedTabId}`}>
                                        <EuiButton size="s" iconType="plus">
                                            Prijs
                                        </EuiButton>
                                    </EuiLink>
                                </EuiFlexItem>
                                {canManagePrijsCombi && (
                                    <EuiFlexItem grow={false}>
                                        <EuiLink href={`/prices`}>
                                            <EuiButton size="s" iconType="pencil">
                                                Prijs Combi
                                            </EuiButton>
                                        </EuiLink>
                                    </EuiFlexItem>
                                )}
                                {selectedTabId === "cannabis" && (
                                    <>
                                        {shopId !== "f155b35a-14a1-4866-867f-93fe21e9769b" && (
                                            <EuiFlexItem grow={false}>
                                                <EuiLink href={"new-product/kind"}>
                                                    <EuiButton size="s" iconType="plus">
                                                        Wiet
                                                    </EuiButton>
                                                </EuiLink>
                                            </EuiFlexItem>
                                        )}

                                        {showAdminLinks && (
                                            <>
                                                <EuiFlexItem grow={false}>
                                                    <EuiLink href={"manage/strains-tags"}>
                                                        <EuiButton size="s" iconType="pencil">
                                                            Kruisingen & Tags
                                                        </EuiButton>
                                                    </EuiLink>
                                                </EuiFlexItem>
                                            </>
                                        )}
                                    </>
                                )}
                                <EuiFlexItem grow={false}>
                                    <EuiLink onClick={() => setSearchIsOpen(!searchIsOpen)}>
                                        <EuiIcon type="search" size="l" />
                                    </EuiLink>
                                </EuiFlexItem>
                            </EuiFlexGroup>
                        </EuiFlexItem>
                    </EuiFlexGroup>
                    <EuiSpacer size="s" />
                    <ProductResults
                        selectedScreenId={selectedScreenId}
                        searchIsOpen={searchIsOpen}
                        mode={selectedTabId}
                    />
                </EuiPanel>
            </EuiPageBody>
        </EuiPage>
    );
});

Products.displayName = "Products";
export default Products;
